import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './FreeTrialBadge.css';

const FreeTrialBadge = ({ trialExpiryDate, subscription, email, subscriptionSource }) => {
  // 'window' = INVEXIS set a fixed 1-month access window; otherwise the 30-day trial.
  const isFixedWindow = subscriptionSource === 'window';
  const [showPopup, setShowPopup] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const expiryMs = trialExpiryDate ? new Date(trialExpiryDate).getTime() : 0;

  const calcTimeLeft = useCallback(() => {
    if (!expiryMs) return null;
    const now = Date.now();
    const diff = expiryMs - now;

    if (diff <= 0) return { expired: true };

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { expired: false, days, hours, minutes, seconds, totalSeconds };
  }, [expiryMs]);

  useEffect(() => {
    const tl = calcTimeLeft();
    setTimeLeft(tl);

    if (!tl || tl.expired) return;

    // If less than 24 hours, tick every second for live countdown
    const interval = tl.days === 0 ? 1000 : 60000;
    const timer = setInterval(() => {
      setTimeLeft(calcTimeLeft());
    }, interval);

    return () => clearInterval(timer);
  }, [calcTimeLeft]);

  // Don't show if user has paid subscription, or no trial data, or trial expired
  if (subscription === true) return null;
  if (!trialExpiryDate) return null;
  if (!timeLeft || timeLeft.expired) return null;

  return (
    <>
      <button
        className="free-trial-badge"
        onClick={(e) => {
          e.stopPropagation();
          setShowPopup(true);
        }}
      >
        {isFixedWindow ? 'Subscription' : 'Free Trial'}
      </button>

      {showPopup && ReactDOM.createPortal(
        <div className="trial-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="trial-popup-box" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="trial-popup-close" onClick={() => setShowPopup(false)}>
              ✕
            </button>

            {/* Icon */}
            <div className="trial-popup-icon-wrap">
              <div className="trial-popup-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="trial-popup-title">{isFixedWindow ? 'Subscription Active' : 'Free Trial Active'}</h3>

            {/* Subtitle */}
            <p className="trial-popup-subtitle">
              {isFixedWindow
                ? `Your INVEXIS access is active until ${new Date(trialExpiryDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.`
                : 'You are currently using a free trial of INVEXIS.'}
            </p>

            {/* Countdown */}
            <div className={`trial-popup-countdown ${timeLeft.days === 0 ? 'trial-popup-countdown-urgent' : ''}`}>
              <span className="trial-popup-countdown-value">
                {timeLeft.days > 0 ? timeLeft.days : (
                  <span className="trial-popup-timer">
                    {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
                  </span>
                )}
              </span>
              <span className="trial-popup-countdown-label">
                {timeLeft.days > 0 ? `day${timeLeft.days !== 1 ? 's' : ''} remaining` : 'remaining'}
              </span>
            </div>

            {/* Info text */}
            <p className="trial-popup-info">
              {isFixedWindow
                ? 'When this period ends, you\'ll need to renew to continue using all features.'
                : "After the trial ends, you'll need to subscribe to continue using all features."}
            </p>

            {/* Action button */}
            <button
              className="trial-popup-action"
              onClick={() => {
                const queryParams = new URLSearchParams();
                queryParams.append('plan', 'atomic');
                if (email) {
                  queryParams.append('email', email);
                }
                window.open(`https://welcome.invexis.co.in/?${queryParams.toString()}#pricing`, '_blank');
                setShowPopup(false);
              }}
            >
              View Plans
            </button>


          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default FreeTrialBadge;
